/**
 * Read a legacy .xls workbook (Excel 97–2003).
 *
 * Neither ExcelJS nor the .xlsb reader can touch this. `.xls` is BIFF8 inside an
 * OLE2 Compound File - a little FAT filesystem in a single file - which is a
 * completely different container from the zip that .xlsx and .xlsb both use.
 *
 * It is worth supporting rather than telling people to convert, because a tool
 * room that has been running since the nineties has drawers of .xls, and "open
 * it in Excel and save it again" is a step somebody does wrong once and then
 * imports the wrong file.
 *
 * ## The two layers
 *
 * **OLE2**: a 512-byte header, then sectors. A FAT chains sectors together, a
 * directory lists the streams, and small streams live packed inside a "mini
 * stream" with its own FAT. We need one stream: `Workbook`.
 *
 * **BIFF8**: that stream is a record sequence - `[id: 2 bytes][length: 2
 * bytes][payload]`. Strings live in a shared table (SST); cells reference it by
 * index. Numbers are either RK-encoded or full doubles.
 *
 * Images are not read. They are in an Escher/MSODRAWING blob whose anchor
 * records are a different format again, and a legacy sheet that also has
 * embedded photos is rare enough not to justify it. Text imports; photos do not.
 */
// --- OLE2 ---
const OLE_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
const END_OF_CHAIN = 0xfffffffe;
const FREE_SECTOR = 0xffffffff;
/** True when these bytes are an OLE2 compound file, which is what .xls is. */
export function isOleFile(data) {
    if (data.length < 8)
        return false;
    return OLE_SIGNATURE.every((b, i) => data[i] === b);
}
function sectorOffset(ctx, sector) {
    // Sector 0 starts immediately after the 512-byte header.
    return 512 + sector * ctx.sectorSize;
}
/** Walk a FAT chain and concatenate its sectors. */
function readChain(ctx, start, size, mini) {
    const unit = mini ? ctx.miniSectorSize : ctx.sectorSize;
    const table = mini ? ctx.miniFat : ctx.fat;
    const out = new Uint8Array(size);
    let written = 0;
    let sector = start;
    // A corrupt chain can loop; the sector count is a hard bound on honest ones.
    let guard = table.length + 8;
    while (sector !== END_OF_CHAIN && sector !== FREE_SECTOR && written < size && guard-- > 0) {
        const from = mini ? sector * ctx.miniSectorSize : sectorOffset(ctx, sector);
        const src = mini ? ctx.miniStream : ctx.data;
        const take = Math.min(unit, size - written);
        if (from + take > src.length)
            break;
        out.set(src.subarray(from, from + take), written);
        written += take;
        sector = table[sector] ?? END_OF_CHAIN;
    }
    return out.subarray(0, written);
}
/** UTF-16LE directory entry name, trimmed at its terminator. */
function entryName(view, offset, byteLength) {
    let out = '';
    for (let i = 0; i + 1 < byteLength; i += 2) {
        const code = view.getUint16(offset + i, true);
        if (code === 0)
            break;
        out += String.fromCharCode(code);
    }
    return out;
}
/**
 * Pull one named stream out of an OLE2 file.
 *
 * Returns null when the stream is not there, so the caller can say something
 * useful rather than crashing on an undefined.
 */
export function readOleStream(data, wanted) {
    if (!isOleFile(data))
        return null;
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    const sectorSize = 1 << view.getUint16(30, true);
    const miniSectorSize = 1 << view.getUint16(32, true);
    const fatSectorCount = view.getUint32(44, true);
    const dirStart = view.getUint32(48, true);
    const miniCutoff = view.getUint32(56, true);
    const miniFatStart = view.getUint32(60, true);
    const miniFatCount = view.getUint32(64, true);
    const difStart = view.getUint32(68, true);
    const difCount = view.getUint32(72, true);
    const ctx = {
        view,
        data,
        sectorSize,
        miniSectorSize,
        fat: [],
        miniFat: [],
        miniStream: new Uint8Array(0),
    };
    // --- FAT sector list: 109 entries in the header, the rest via the DIFAT ---
    const fatSectors = [];
    for (let i = 0; i < Math.min(fatSectorCount, 109); i++) {
        const s = view.getUint32(76 + i * 4, true);
        if (s !== FREE_SECTOR)
            fatSectors.push(s);
    }
    let difSector = difStart;
    let difLeft = difCount;
    const perDif = sectorSize / 4 - 1;
    while (difLeft-- > 0 && difSector !== END_OF_CHAIN && difSector !== FREE_SECTOR) {
        const base = sectorOffset(ctx, difSector);
        if (base + sectorSize > data.length)
            break;
        for (let i = 0; i < perDif; i++) {
            const s = view.getUint32(base + i * 4, true);
            if (s !== FREE_SECTOR)
                fatSectors.push(s);
        }
        difSector = view.getUint32(base + perDif * 4, true);
    }
    for (const s of fatSectors) {
        const base = sectorOffset(ctx, s);
        if (base + sectorSize > data.length)
            break;
        for (let i = 0; i < sectorSize / 4; i++)
            ctx.fat.push(view.getUint32(base + i * 4, true));
    }
    // --- mini FAT ---
    if (miniFatStart !== END_OF_CHAIN && miniFatStart !== FREE_SECTOR) {
        const bytes = readChain(ctx, miniFatStart, miniFatCount * sectorSize, false);
        const mv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        for (let i = 0; i + 4 <= bytes.length; i += 4)
            ctx.miniFat.push(mv.getUint32(i, true));
    }
    // --- directory ---
    const dirBytes = readChain(ctx, dirStart, ctx.fat.length * sectorSize, false);
    const dv = new DataView(dirBytes.buffer, dirBytes.byteOffset, dirBytes.byteLength);
    const entries = [];
    for (let off = 0; off + 128 <= dirBytes.length; off += 128) {
        const nameLen = dv.getUint16(off + 64, true);
        const type = dv.getUint8(off + 66);
        if (type === 0)
            continue; // unallocated
        entries.push({
            name: entryName(dv, off, nameLen),
            start: dv.getUint32(off + 116, true),
            // 8-byte size; the high word is irrelevant for anything we can hold in a
            // browser, and reading it as 64-bit would need BigInt for no gain.
            size: dv.getUint32(off + 120, true),
        });
    }
    // Entry 0 is the root, and its stream is where all the small streams live.
    const root = entries[0];
    if (root)
        ctx.miniStream = readChain(ctx, root.start, root.size, false);
    const target = entries.find((e) => e.name === wanted);
    if (!target)
        return null;
    return readChain(ctx, target.start, target.size, target.size < miniCutoff);
}
// --- BIFF8 ---
const BOF = 0x0809;
const EOF_REC = 0x000a;
const DIMENSIONS = 0x0200;
const LABELSST = 0x00fd;
const RK = 0x027e;
const MULRK = 0x00bd;
const NUMBER = 0x0203;
const BOOLERR = 0x0205;
const FORMULA = 0x0006;
const STRING_REC = 0x0207;
const SST = 0x00fc;
const CONTINUE = 0x003c;
const LABEL = 0x0204;
/** Same RK trick as BIFF12: a double squeezed into 32 bits. */
function rkValue(rk) {
    const isX100 = (rk & 1) !== 0;
    const isInt = (rk & 2) !== 0;
    let value;
    if (isInt) {
        const raw = rk >> 2;
        value = raw >= 0x20000000 ? raw - 0x40000000 : raw;
    }
    else {
        const buf = new ArrayBuffer(8);
        const dv = new DataView(buf);
        dv.setUint32(4, rk & 0xfffffffc, true);
        value = dv.getFloat64(0, true);
    }
    return isX100 ? value / 100 : value;
}
/**
 * Read the shared string table.
 *
 * The awkward part is CONTINUE: the SST is often larger than one record, and a
 * string can be cut in half across the boundary - including between a string's
 * header and its characters. The continuation then carries a fresh flag byte
 * saying how the *rest* is encoded, which may differ from the first half.
 */
function readSst(records, startIndex) {
    const first = records[startIndex][1];
    // Concatenate the SST with every CONTINUE that follows, remembering where each
    // block began so the flag byte at a boundary can be honoured.
    const blocks = [first];
    for (let i = startIndex + 1; i < records.length; i++) {
        if (records[i][0] !== CONTINUE)
            break;
        blocks.push(records[i][1]);
    }
    const total = blocks.reduce((n, b) => n + b.length, 0);
    const buf = new Uint8Array(total);
    const boundaries = [];
    let at = 0;
    for (const b of blocks) {
        if (at > 0)
            boundaries.push(at);
        buf.set(b, at);
        at += b.length;
    }
    const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    const count = view.getUint32(4, true);
    const out = [];
    let p = 8;
    for (let n = 0; n < count && p + 3 <= buf.length; n++) {
        const chars = view.getUint16(p, true);
        let flags = buf[p + 2];
        p += 3;
        let wide = (flags & 0x01) !== 0;
        const hasRich = (flags & 0x08) !== 0;
        const hasPhonetic = (flags & 0x04) !== 0;
        let richRuns = 0;
        let phoneticBytes = 0;
        if (hasRich) {
            richRuns = view.getUint16(p, true);
            p += 2;
        }
        if (hasPhonetic) {
            phoneticBytes = view.getUint32(p, true);
            p += 4;
        }
        let text = '';
        let read = 0;
        while (read < chars && p < buf.length) {
            // A block boundary resets the encoding for the remainder of this string.
            if (boundaries.includes(p)) {
                flags = buf[p];
                wide = (flags & 0x01) !== 0;
                p += 1;
            }
            if (wide) {
                if (p + 1 >= buf.length)
                    break;
                text += String.fromCharCode(view.getUint16(p, true));
                p += 2;
            }
            else {
                text += String.fromCharCode(buf[p]);
                p += 1;
            }
            read++;
        }
        p += richRuns * 4 + phoneticBytes;
        out.push(text);
    }
    return out;
}
/**
 * Parse a legacy .xls into the first worksheet's cells.
 *
 * Only the first sheet, for the same reason as the .xlsb reader: a mould list is
 * one sheet, and guessing which of several is "the data" is a worse failure than
 * saying so.
 */
export function parseXls(data) {
    const stream = readOleStream(data, 'Workbook') ?? readOleStream(data, 'Book');
    if (!stream) {
        throw new Error('That .xls could not be opened - no workbook stream inside it. If Excel can open it, saving it as .xlsx will work.');
    }
    // --- record scan ---
    const view = new DataView(stream.buffer, stream.byteOffset, stream.byteLength);
    const records = [];
    let p = 0;
    while (p + 4 <= stream.length) {
        const id = view.getUint16(p, true);
        const len = view.getUint16(p + 2, true);
        if (p + 4 + len > stream.length)
            break;
        records.push([id, stream.subarray(p + 4, p + 4 + len)]);
        p += 4 + len;
    }
    // The globals substream holds the SST; the sheet substreams follow it.
    let sst = [];
    for (let i = 0; i < records.length; i++) {
        if (records[i][0] === SST) {
            sst = readSst(records, i);
            break;
        }
    }
    const cells = new Map();
    let maxRow = 0;
    let maxCol = 0;
    const put = (row, col, value) => {
        if (value === null || value === '')
            return;
        cells.set(`${row},${col}`, value);
        if (row > maxRow)
            maxRow = row;
        if (col > maxCol)
            maxCol = col;
    };
    // Cell records live in the first worksheet substream. Everything before the
    // second BOF is globals; everything after the following EOF is another sheet.
    let bofSeen = 0;
    let inSheet = false;
    let lastFormulaCell = null;
    for (let i = 0; i < records.length; i++) {
        const [id, payload] = records[i];
        const rv = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
        if (id === BOF) {
            bofSeen++;
            if (bofSeen === 2)
                inSheet = true;
            continue;
        }
        if (id === EOF_REC && inSheet)
            break;
        if (!inSheet)
            continue;
        switch (id) {
            case LABELSST: {
                if (payload.length < 10)
                    break;
                const row = rv.getUint16(0, true);
                const col = rv.getUint16(2, true);
                const idx = rv.getUint32(6, true);
                put(row, col, sst[idx] ?? '');
                break;
            }
            case LABEL: {
                // An inline string, used by some writers instead of the SST.
                if (payload.length < 8)
                    break;
                const row = rv.getUint16(0, true);
                const col = rv.getUint16(2, true);
                const chars = rv.getUint16(6, true);
                const wide = payload.length > 8 && (payload[8] & 0x01) !== 0;
                let text = '';
                let q = 9;
                for (let c = 0; c < chars && q < payload.length; c++) {
                    if (wide) {
                        text += String.fromCharCode(rv.getUint16(q, true));
                        q += 2;
                    }
                    else {
                        text += String.fromCharCode(payload[q]);
                        q += 1;
                    }
                }
                put(row, col, text);
                break;
            }
            case RK: {
                if (payload.length < 10)
                    break;
                put(rv.getUint16(0, true), rv.getUint16(2, true), rkValue(rv.getUint32(6, true)));
                break;
            }
            case MULRK: {
                // One record carrying a run of RK cells across consecutive columns.
                if (payload.length < 6)
                    break;
                const row = rv.getUint16(0, true);
                const firstCol = rv.getUint16(2, true);
                const count = Math.floor((payload.length - 6) / 6);
                for (let k = 0; k < count; k++) {
                    put(row, firstCol + k, rkValue(rv.getUint32(4 + k * 6 + 2, true)));
                }
                break;
            }
            case NUMBER: {
                if (payload.length < 14)
                    break;
                put(rv.getUint16(0, true), rv.getUint16(2, true), rv.getFloat64(6, true));
                break;
            }
            case BOOLERR: {
                if (payload.length < 8)
                    break;
                // Byte 7 distinguishes a boolean from an error code; errors are dropped
                // rather than imported as a number that means nothing.
                if (payload[7] === 0)
                    put(rv.getUint16(0, true), rv.getUint16(2, true), payload[6] !== 0);
                break;
            }
            case FORMULA: {
                if (payload.length < 14)
                    break;
                const row = rv.getUint16(0, true);
                const col = rv.getUint16(2, true);
                // A formula's cached result is a double unless the top two bytes are
                // 0xFFFF, which flags a string, boolean or error instead.
                if (rv.getUint16(12, true) === 0xffff) {
                    const kind = payload[6];
                    if (kind === 1)
                        put(row, col, payload[8] !== 0);
                    else if (kind === 0)
                        lastFormulaCell = { row, col }; // string follows
                }
                else {
                    put(row, col, rv.getFloat64(6, true));
                }
                break;
            }
            case STRING_REC: {
                // The cached string result of the FORMULA immediately before.
                if (!lastFormulaCell || payload.length < 3)
                    break;
                const chars = rv.getUint16(0, true);
                const wide = (payload[2] & 0x01) !== 0;
                let text = '';
                let q = 3;
                for (let c = 0; c < chars && q < payload.length; c++) {
                    if (wide) {
                        text += String.fromCharCode(rv.getUint16(q, true));
                        q += 2;
                    }
                    else {
                        text += String.fromCharCode(payload[q]);
                        q += 1;
                    }
                }
                put(lastFormulaCell.row, lastFormulaCell.col, text);
                lastFormulaCell = null;
                break;
            }
            case DIMENSIONS: {
                if (payload.length >= 10) {
                    maxRow = Math.max(maxRow, rv.getUint32(4, true));
                    maxCol = Math.max(maxCol, rv.getUint16(8, true));
                }
                break;
            }
            default:
                break;
        }
    }
    return {
        name: 'Sheet 1',
        rowCount: maxRow + 1,
        columnCount: maxCol + 1,
        cell: (row, col) => cells.get(`${row - 1},${col - 1}`) ?? null,
    };
}
//# sourceMappingURL=xls.js.map